import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Column } from 'react-table';
import { useTable } from 'react-table';
import * as actions from '../_redux/notificationsActions';
import * as commonColumnFormatters from '../../_common/column-formatters';
import * as columnFormatters from './column-formatters';
import { useNotificationsContext } from '../context/NotificationsContext';
import useInfiniteScrollControl from '../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from "../../_common/components/CommonGrid/CommonGrid";

export interface Sender {
  agentId?: string;
  firstName?: string;
  lastName?: string;
}

export interface Receiver {
  customerId?: string;
  customerName?: string;
}

export interface NotificationRow {
  sentAt?: string | Date | JSX.Element;
  subject?: string | JSX.Element;
  type?: string | JSX.Element | null;
  target?: string | JSX.Element;
  status?: string | JSX.Element;
  sender?: Sender | JSX.Element;
  receiver?: Receiver | JSX.Element;
}

export function NotificationsTable() {
  const prefix = 'notifications-column';
  const intl = useIntl();
  const [tableRows, setTableRows] = useState<NotificationRow[]>([]);

  const columns: Column<NotificationRow>[] = useMemo(() => [
    {
      accessor: 'sentAt',
      Header: intl.formatMessage({ id: `${prefix}-sentAt`, defaultMessage: 'Send at' })
    },
    {
      accessor: 'subject',
      Header: intl.formatMessage({ id: `${prefix}-subject`, defaultMessage: 'Subject' })
    },
    {
      accessor: 'type',
      Header: intl.formatMessage({ id: `${prefix}-type`, defaultMessage: 'Type' })
    },
    {
      accessor: 'target',
      Header: intl.formatMessage({ id: `${prefix}-target`, defaultMessage: 'Target' })
    },
    {
      accessor: 'status',
      Header: intl.formatMessage({ id: `${prefix}-status`, defaultMessage: 'Status' })
    },
    {
      accessor: 'sender',
      Header: intl.formatMessage({ id: `${prefix}-sender`, defaultMessage: 'Sender' })
    },
    {
      accessor: 'receiver',
      Header: intl.formatMessage({ id: `${prefix}-receiver`, defaultMessage: 'Receiver' })
    }
  ], [intl]);

  const data = useMemo(
    () =>
      tableRows.map((item: any) => ({
        sentAt: commonColumnFormatters.DateColumnFormatter(item.sentAt, true),
        subject: commonColumnFormatters.BoldColumnFormatter(item.subject),
        type: columnFormatters.TypeColumnFormatter(item.type),
        target: commonColumnFormatters.BoldColumnFormatter(item.target),
        status: columnFormatters.StatusColumnFormatter(item.status, item.statusText),
        sender: columnFormatters.PersonColumnFormatter(item?.sender ?? {agentID: '', firstName: '', lastName: ''}),
        receiver: columnFormatters.PersonColumnFormatter(item?.receiver ?? {agentID: '', firstName: '', lastName: ''}),
      })),
    [tableRows]
  );

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'notifications',
    UIContext: useNotificationsContext(),
    getNewEntities: actions.getNotifications,
    getNextEntities: actions.getNextNotifications,
    onEntitiesChange: (entities: NotificationRow[]) => setTableRows(entities),
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <div className="spinner-container">
        <span className="spinner spinner-md spinner-primary" />
      </div>
    ),
    style: {
      overflow: 'inherit',
    },
  });

  const tableInstance = useTable<NotificationRow>({
    columns,
    data,
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      tableProps={tableInstance}
      customTableStyles="notifications-table"
    />
  );
}